import { FC, useState } from 'react';
import {
  Button,
  Spacer,
  Stack,
  TextSubtitle,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '..';
import { useUploadCSV } from '../../../hooks';

export type CsvUploadModalProps = GenericModalProps & {
  file?: File | null;
  onCancel?: () => void;
};

export const CsvUploadModal: FC<CsvUploadModalProps> = ({
  file,
  modalRef,
  onClose: onCloseModal,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const { handleUpload } = useUploadCSV();

  const handleFileRead = async (fileForProcessing?: File | null) => {
    if (!fileForProcessing) return;

    setIsUploading(true);

    const handleSuccess = () => {
      onCloseModal?.();
      setIsUploading(false);
    };

    try {
      await handleUpload?.([fileForProcessing], handleSuccess);
    } catch (err) {
      console.error(err);
      setIsUploading(false);
    }
  };

  return (
    <GenericModal
      test-id="ps-csv-upload-modal"
      modalRef={modalRef}
      onClose={onCloseModal}
      hideCloseBtn
      stayOnClickOutside
      stayOnEsc
    >
      <TextTitle size="small">
        <TranslationComponent i18nKey="pro-studio/workflows:csv_upload_modal.title">
          Replace all nodes in this solution?
        </TranslationComponent>
      </TextTitle>
      <Spacer size="small" />
      <TextSubtitle size="small">
        <TranslationComponent i18nKey="pro-studio/workflows:csv_upload_modal.subtitle">
          Importing this CSV will replace all nodes in this solution.
        </TranslationComponent>
      </TextSubtitle>
      <Spacer size="small" />
      <Stack justifyContent="end" gutter="small" slot="footer">
        <Button
          test-id="ps-csv-upload-modal-cancel-btn"
          size="large"
          variant="secondary"
          onClick={onCloseModal}
          disabled={isUploading}
        >
          <TranslationComponent i18nKey="pro-studio/workflows:csv_upload_modal.buttons.cancel">
            Cancel
          </TranslationComponent>
        </Button>
        <Button
          test-id="ps-csv-upload-modal-replace-nodes-btn"
          size="large"
          onClick={() => handleFileRead(file)}
          disabled={isUploading}
        >
          <TranslationComponent
            i18nKey={`pro-studio/workflows:csv_upload_modal.buttons.${isUploading ? 'uploading' : 'save'}`}
          >
            {isUploading ? 'Uploading...' : 'Replace Nodes'}
          </TranslationComponent>
        </Button>
      </Stack>
    </GenericModal>
  );
};
